<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useStoreGetters, useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import CampaignsAPI from 'dashboard/api/campaigns';
import InboxHealthAPI from 'dashboard/api/inboxHealth';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';
import WhatsAppCampaignDeliveryReport from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDeliveryReport.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
const editingCampaign = ref(null);
const [showWhatsAppCampaignDialog, toggleWhatsAppCampaignDialog] = useToggle();
const [showDeliveryReport, toggleDeliveryReport] = useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);

const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const qualityRating = ref('');

const fetchHealthStatus = async inboxId => {
  if (!inboxId) return;
  try {
    const { data } = await InboxHealthAPI.getHealthStatus(inboxId);
    qualityRating.value = data.quality_rating || '';
  } catch {
    qualityRating.value = '';
  }
};

watch(
  WhatsAppCampaigns,
  campaigns => {
    if (campaigns?.length > 0) {
      const firstInbox = campaigns[0]?.inbox;
      if (firstInbox?.id) {
        fetchHealthStatus(firstInbox.id);
      }
    }
  },
  { immediate: true }
);

const handleEdit = campaign => {
  editingCampaign.value = campaign;
  toggleWhatsAppCampaignDialog(true);
};

const handleNewCampaign = () => {
  editingCampaign.value = null;
  toggleWhatsAppCampaignDialog(true);
};

const handleCloseDialog = () => {
  editingCampaign.value = null;
  toggleWhatsAppCampaignDialog(false);
};

const handleDelete = campaign => {
  confirmDeleteCampaignDialogRef.value?.open(campaign);
};

const handleViewReport = campaign => {
  selectedCampaign.value = campaign;
  toggleDeliveryReport(true);
};

const handleCloseDeliveryReport = () => {
  toggleDeliveryReport(false);
  selectedCampaign.value = null;
};

const handleCampaignDeleted = () => {
  selectedCampaign.value = null;
};

const refreshCampaigns = () => store.dispatch('campaigns/get');

const handleResume = async campaign => {
  if (
    !confirm(
      `Continuar envios da campanha "${campaign.title}"?\n\nOs contatos que já receberam serão ignorados.`
    )
  ) {
    return;
  }
  try {
    await CampaignsAPI.resume(campaign.id);
    useAlert(
      'Envios retomados com sucesso! A campanha continuará de onde parou.'
    );
    refreshCampaigns();
  } catch (error) {
    const msg = error?.response?.data?.error || 'Erro ao continuar campanha';
    useAlert(msg);
  }
};

const handlePause = async campaign => {
  if (!confirm(`Pausar envios da campanha "${campaign.title}"?`)) {
    return;
  }
  try {
    await CampaignsAPI.pause(campaign.id);
    useAlert('Campanha pausada com sucesso.');
    refreshCampaigns();
  } catch (error) {
    const msg = error?.response?.data?.error || 'Erro ao pausar campanha';
    useAlert(msg);
  }
};

const handleRestart = async campaign => {
  if (
    !confirm(
      `Reiniciar campanha "${campaign.title}" do zero?\n\nTodo o histórico de envios será limpo e a campanha enviará para todos os contatos novamente.`
    )
  ) {
    return;
  }
  try {
    const { data } = await CampaignsAPI.restart(campaign.id);
    useAlert(data.message || 'Campanha reiniciada do zero!');
    refreshCampaigns();
  } catch (error) {
    const msg = error?.response?.data?.error || 'Erro ao reiniciar campanha';
    useAlert(msg);
  }
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    @click="handleNewCampaign"
    @close="handleCloseDialog"
  >
    <template #action>
      <WhatsAppCampaignDialog
        v-if="showWhatsAppCampaignDialog"
        :campaign="editingCampaign"
        @close="handleCloseDialog"
      />
    </template>
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoWhatsAppCampaigns"
      :campaigns="WhatsAppCampaigns"
      :quality-rating="qualityRating"
      @edit="handleEdit"
      @delete="handleDelete"
      @view-report="handleViewReport"
      @resume="handleResume"
      @pause="handlePause"
      @restart="handleRestart"
    />
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      @deleted="handleCampaignDeleted"
    />
    <WhatsAppCampaignDeliveryReport
      v-if="selectedCampaign"
      :campaign="selectedCampaign"
      :is-open="showDeliveryReport"
      @close="handleCloseDeliveryReport"
    />
  </CampaignLayout>
</template>
